import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Alert } from "../../components/Alert";
import { ItemsEditor } from "../../components/billing/ItemsEditor";
import { RequirePermission } from "../../components/crm/RequirePermission";
import { Spinner } from "../../components/Spinner";
import {
  billingErrorCode,
  createQuote,
  getQuote,
  isApiError,
  listAccounts,
  listProducts,
  listTaxes,
  updateQuote,
} from "../../lib/billing-api";
import { draftFromItem, emptyItemDraft, toDocumentItemInputs, type ItemDraft } from "../../lib/billing-items";
import type { Account, Product, QuoteInput, Tax } from "../../lib/billing-types";
import type { TranslationKey } from "../../lib/i18n";
import { useConfig } from "../../state/config-context";

const ERROR_KEYS: Record<string, TranslationKey> = {
  invalid_state_transition: "billing.error.invalidTransition",
  "billing.error.invalid_state_transition": "billing.error.invalidTransition",
  "billing.error.currency_mismatch": "billing.error.currencyMismatch",
  billing_item_immutable: "billing.error.itemImmutable",
  billing_quote_not_found: "billing.error.notFound",
};

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function QuoteFormInner({ id }: { id: string | undefined }): ReactNode {
  const { t } = useConfig();
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [accountId, setAccountId] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [issueDate, setIssueDate] = useState(today());
  const [validUntil, setValidUntil] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState<ItemDraft[]>([emptyItemDraft("item-0")]);
  const [loading, setLoading] = useState(id !== undefined);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [accountPage, productPage, taxPage] = await Promise.all([
          listAccounts(),
          listProducts(),
          listTaxes(),
        ]);
        if (!cancelled) {
          setAccounts(accountPage.items);
          setProducts(productPage.items);
          setTaxes(taxPage.items);
        }
      } catch (caught) {
        if (!cancelled) {
          setError(isApiError(caught) ? caught.detail : t("error.network"));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    let cancelled = false;
    if (id === undefined) {
      return () => {
        cancelled = true;
      };
    }
    void (async () => {
      setLoading(true);
      try {
        const quote = await getQuote(id);
        if (cancelled) {
          return;
        }
        if (quote.status !== "draft") {
          setError(t("billing.form.quoteNotEditable"));
          return;
        }
        setAccountId(quote.account_id);
        setCurrency(quote.currency);
        setIssueDate(quote.issue_date.slice(0, 10));
        setValidUntil(quote.valid_until === null ? "" : quote.valid_until.slice(0, 10));
        setNotes(quote.notes ?? "");
        setItems(
          quote.items.length === 0
            ? [emptyItemDraft("item-0")]
            : quote.items.map((item, index) => draftFromItem(item, `item-${String(index)}`)),
        );
      } catch (caught) {
        if (!cancelled) {
          setError(isApiError(caught) ? caught.detail : t("error.network"));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, t]);

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    const input: QuoteInput = {
      account_id: accountId,
      currency: currency.trim().toUpperCase(),
      issue_date: issueDate,
      valid_until: validUntil === "" ? null : validUntil,
      notes: notes.trim() === "" ? null : notes.trim(),
      items: toDocumentItemInputs(items, taxes),
    };
    try {
      if (id === undefined) {
        const quote = await createQuote(input);
        navigate(`/billing/quotes/${quote.id}`, { replace: true });
      } else {
        await updateQuote(id, input);
        navigate(`/billing/quotes/${id}`, { replace: true });
      }
    } catch (caught) {
      const code = billingErrorCode(caught);
      const key = code === null ? undefined : ERROR_KEYS[code];
      setError(
        key !== undefined ? t(key) : isApiError(caught) ? caught.detail : t("error.network"),
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Spinner label={t("app.loading")} />;
  }

  return (
    <section className="page" aria-labelledby="billing-quote-form-title">
      <header className="page__header">
        <p className="muted">
          <Link to="/billing/quotes">{t("billing.quotes.title")}</Link>
        </p>
        <h1 id="billing-quote-form-title">
          {id === undefined ? t("billing.form.createQuote") : t("billing.form.editQuote")}
        </h1>
      </header>

      {error !== null ? <Alert tone="error">{error}</Alert> : null}

      <form className="form form--inline-card" onSubmit={(event) => void onSubmit(event)}>
        <div className="form-grid">
          <label className="field" htmlFor="quote-account">
            <span>{t("billing.quote.account")}</span>
            <select
              id="quote-account"
              value={accountId}
              required
              onChange={(event) => {
                const next = event.target.value;
                setAccountId(next);
                const account = accounts.find((row) => row.id === next);
                if (account !== undefined) {
                  setCurrency(account.currency);
                }
              }}
            >
              <option value="">{t("billing.form.accountRequired")}</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name} · {account.currency}
                </option>
              ))}
            </select>
          </label>
          <label className="field" htmlFor="quote-currency">
            <span>{t("billing.common.currency")}</span>
            <input
              id="quote-currency"
              value={currency}
              maxLength={3}
              required
              onChange={(event) => setCurrency(event.target.value)}
            />
          </label>
          <label className="field" htmlFor="quote-issue">
            <span>{t("billing.quote.issueDate")}</span>
            <input
              id="quote-issue"
              type="date"
              value={issueDate}
              onChange={(event) => setIssueDate(event.target.value)}
            />
          </label>
          <label className="field" htmlFor="quote-valid">
            <span>{t("billing.quote.validUntil")}</span>
            <input
              id="quote-valid"
              type="date"
              value={validUntil}
              onChange={(event) => setValidUntil(event.target.value)}
            />
          </label>
          <label className="field" htmlFor="quote-notes">
            <span>{t("billing.quote.notes")}</span>
            <textarea
              id="quote-notes"
              value={notes}
              rows={3}
              maxLength={5000}
              onChange={(event) => setNotes(event.target.value)}
            />
          </label>
        </div>

        <h2 className="section-title">{t("billing.quote.items")}</h2>
        <ItemsEditor
          items={items}
          products={products}
          taxes={taxes}
          busy={saving}
          onChange={setItems}
        />
        <p className="muted">{t("billing.item.totalsHint")}</p>

        <div className="form-actions">
          <button
            type="submit"
            className="btn btn--primary"
            disabled={saving || accountId === "" || items.length === 0}
          >
            {saving ? t("billing.form.submitting") : t("billing.form.submit")}
          </button>
          <Link className="btn btn--ghost" to="/billing/quotes">
            {t("common.cancel")}
          </Link>
        </div>
      </form>
    </section>
  );
}

function QuoteFormRoute(): ReactNode {
  const { id } = useParams<{ id: string }>();
  return (
    <RequirePermission permission={id === undefined ? "billing.quote.create" : "billing.quote.update"}>
      <QuoteFormInner id={id} />
    </RequirePermission>
  );
}

export function QuoteFormPage(): ReactNode {
  return <QuoteFormRoute />;
}
